import { useCallback, useContext, useMemo, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import GenericTable from '../table/GenericTable'
import { ReportingContext } from '../../../../../wrappers/ReportingContext'
import { GroupByColumnsContext } from './GroupByColumnsContext'
import EmptyTitleHeader from '../table/TitleHeader'
import { convertReactSelectObjectToValue } from '../../../../../../utils/reporting/config'

const hasAggregateFunction = (col, reportColumns) => {
    const matchingColumns = reportColumns.filter((c) => c.sourceColumn === col)
    return matchingColumns.some((c) => c.aggregateFunction)
}

const createFieldObject = (col, sourceColumn, index, columnsInSelect) => {
    const inSelectValue =
        columnsInSelect.length === 0 || columnsInSelect.includes(sourceColumn)
            ? 'Yes'
            : 'No'

    return {
        id: `${sourceColumn}[${index}]`,
        inSelect: inSelectValue,
        sourceColumn: sourceColumn,
        columnType: col.DATA_TYPE_DISPLAY,
    }
}

const GroupByAvailableColumns = [
    {
        Header: 'In SELECT?',
        accessor: 'inSelect',
        Cell: ({ row: { original } }) => {
            return original.inSelect
        },
    },
    {
        Header: 'Source.Column',
        accessor: 'sourceColumn',
        Cell: ({ value }) => {
            return (
                value && (
                    <span>
                        {value.split('.')[0]}.<b>{value.split('.')[1]}</b>
                    </span>
                )
            )
        },
    },
    {
        Header: 'Column Type',
        accessor: 'columnType',
        Cell: ({ value }) => {
            return value
        },
    },
]

const GroupByAvailableColumnsTable = () => {
    const { reportSelectOptions, reportConfig } = useContext(ReportingContext)
    const {
        leftTableHighlights,
        setLeftTableHighlights,
        setLeftTableData,
        setRightTableData,
        rightTableData,
    } = useContext(GroupByColumnsContext)
    const { watch } = useFormContext()

    useEffect(() => {
        setLeftTableData(fields)
    }, [fields, setLeftTableData, setRightTableData])

    const handleFilteredRowsChange = useCallback(
        (rowsInfo) => {
            setLeftTableData(rowsInfo.filteredRows)
        },
        [setLeftTableData]
    )

    const selectedDataSourceColumns = useMemo(
        () =>
            reportSelectOptions
                ? reportSelectOptions.selectedDataSourceColumns
                : [],
        [reportSelectOptions]
    )

    const selectedDataSources = useMemo(() => {
        return reportSelectOptions && reportSelectOptions.selectedDataSources
            ? reportSelectOptions.selectedDataSources
            : []
    }, [reportSelectOptions])

    const columnsInSelect = useMemo(() => {
        return reportConfig && reportConfig.columns
            ? reportConfig.columns.map((c) => c.sourceColumn)
            : []
    }, [reportConfig])

    const selectColumnsWithAggregate = useMemo(() => {
        return reportConfig && reportConfig.columns
            ? reportConfig.columns
                  .filter(
                      (col) =>
                          col.aggregateFunction &&
                          col.aggregateFunction !== null &&
                          col.aggregateFunction !== ''
                  )
                  .map((col) => col.sourceColumn)
            : []
    }, [reportConfig])

    const fields = useMemo(() => {
        // Early return for empty select columns
        if (selectColumnsWithAggregate.length === 0) {
            return []
        }

        // Precompute frequently used values
        const rightTableSourceColumns = new Set(
            rightTableData.map((item) => item.sourceColumn)
        )

        const fieldList = []

        selectedDataSources.forEach((selectedSource) => {
            // Filter table columns once
            const tableColumns = selectedDataSourceColumns.filter(
                (col) => col.TABLE_NAME === selectedSource.value
            )

            const tablePrefix = selectedSource.alias || selectedSource.value

            const fieldGroup = tableColumns
                .map((col) => {
                    const fullColumnName = `${tablePrefix}.${col.COLUMN_NAME}`
                    return { col, fullColumnName }
                })
                .filter(
                    ({ fullColumnName }) =>
                        columnsInSelect.includes(fullColumnName) &&
                        !rightTableSourceColumns.has(fullColumnName) &&
                        !hasAggregateFunction(
                            fullColumnName,
                            reportConfig.columns
                        )
                )
                .map(({ col, fullColumnName }, index) =>
                    createFieldObject(
                        col,
                        fullColumnName,
                        index,
                        columnsInSelect
                    )
                )

            fieldList.push(...fieldGroup)
        })

        return fieldList
    }, [
        selectedDataSourceColumns,
        selectedDataSources,
        columnsInSelect,
        rightTableData,
        selectColumnsWithAggregate,
        reportConfig.columns,
    ])

    return (
        <GenericTable
            columns={GroupByAvailableColumns}
            fields={fields}
            tableHeight={'66vh'}
            tableTile={'Available Columns'}
            viewOnly={false}
            onHightlight={setLeftTableHighlights}
            highlightedRows={leftTableHighlights}
            filterable={true}
            onFilteredRowsChange={handleFilteredRowsChange}
        />
    )
}

export default GroupByAvailableColumnsTable
